import React, { useState } from 'react'
import { Info, AlertTriangle, ExternalLink, Package } from 'lucide-react'
import { useLocation } from 'wouter'
import { useInventoryCoverage } from '../lib/data-service'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import { useTenant } from '../contexts/TenantContext'
import AskAgentButton from './AskAgentButton'

interface InventoryCoverageTileProps {
  className?: string
}

export function InventoryCoverageTile({ className = '' }: InventoryCoverageTileProps) {
  const { selectedFiscalWeek } = useFiscalWeekContext()
  const { selectedRetailerId } = useTenant()
  const { data: wosData, isLoading, error, refetch } = useInventoryCoverage(selectedFiscalWeek)
  const [showTooltip, setShowTooltip] = useState(false)
  const [, setLocation] = useLocation()

  const handleDeepDive = () => {
    setLocation('/inventory-coverage-detail')
  }

  // Format WOS value with "w" suffix
  const formatWOS = (value: number | null) => {
    if (value === null) return '—'
    return `${value.toFixed(1)}w`
  }

  // Get color class based on WOS value
  const getWOSColor = (value: number | null) => {
    if (value === null) return 'text-muted-foreground'
    if (value < 2) return 'text-red-600 dark:text-red-400' // danger - low stock
    if (value <= 10) return 'text-green-600 dark:text-green-400' // success - good range
    return 'text-yellow-600 dark:text-yellow-400' // warning - excess
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div className={`card-premium-elevated ${className}`}>
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 bg-gray-200 rounded-lg"></div>
              <div>
                <div className="h-5 bg-gray-200 rounded w-48 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {/* Store Column */}
            <div>
              <div className="h-4 bg-gray-200 rounded w-12 mb-3"></div>
              <div className="h-10 bg-gray-200 rounded w-20 mb-4"></div>
              <div className="space-y-3">
                <div className="h-6 bg-gray-200 rounded w-24"></div>
                <div className="h-6 bg-gray-200 rounded w-28"></div>
              </div>
            </div>
            {/* DC Column */}
            <div>
              <div className="h-4 bg-gray-200 rounded w-8 mb-3"></div>
              <div className="h-10 bg-gray-200 rounded w-20 mb-4"></div>
              <div className="space-y-3">
                <div className="h-6 bg-gray-200 rounded w-24"></div>
                <div className="h-6 bg-gray-200 rounded w-28"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={`card-premium-elevated ${className}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Inventory Coverage</h3>
              <p className="text-sm text-gray-600">Store & DC</p>
            </div>
          </div>
        </div>
        <p className="text-sm text-red-600 mb-4">Failed to load WOS data</p>
        <button 
          onClick={() => refetch()}
          className="text-sm text-blue-600 hover:text-blue-700 underline font-medium"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!wosData) {
    return (
      <div className={`card-premium-elevated ${className}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Package className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Inventory Coverage</h3>
              <p className="text-sm text-gray-600">Store & DC</p>
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-500">No data available</p>
      </div>
    )
  }

  const totalWOS = wosData.wos_total ?? null
  const storeWOS = wosData.wos_store ?? null
  const dcWOS = wosData.wos_dc ?? null
  const status = wosData.status ?? 'unknown'
  const message = wosData.message ?? ''

  return (
    <div className={`card-premium-elevated ${className}`}>
      {/* Header with Info Icon */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Package className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Inventory Coverage</h3>
            <p className="text-sm text-gray-600">Store & DC</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Ask Agent Button */}
          <AskAgentButton
            metric="wos"
            value={wosData?.wos_total ?? undefined}
            week={selectedFiscalWeek?.toString()}
            retailerId={selectedRetailerId}
            variant="icon"
            size="sm"
            tooltip="Ask AI about inventory coverage"
          />
          
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Info className="w-5 h-5" />
            </button>
          
            {/* Tooltip */}
            {showTooltip && (
              <div className="absolute top-6 right-0 w-72 p-4 bg-popover border border-border rounded-lg shadow-xl z-50 text-xs space-y-2">
                <h4 className="font-semibold text-popover-foreground">How we compute WOS</h4>
                <p className="text-popover-foreground">• WOS = Ending on-hand ÷ weekly units</p>
                <p className="text-popover-foreground">• Store & DC values are read directly from summary data</p>
                <p className="text-popover-foreground">• Total coverage blends store and DC on-hand</p>
                <p className="text-muted-foreground pt-2 border-t border-border">
                  Week {wosData.meta?.fiscal_week ?? selectedFiscalWeek} • Data version {wosData.meta?.data_version ?? '1.0'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Coverage Snapshot */}
      <div className="grid grid-cols-3 gap-6">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-2">Total WOS</p>
          <p className={`text-3xl font-bold ${getWOSColor(totalWOS)}`}>{formatWOS(totalWOS)}</p>
          {message && <p className="text-xs text-muted-foreground mt-2">{message}</p>}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-600 mb-2">Store WOS</p>
          <p className={`text-3xl font-bold ${getWOSColor(storeWOS)}`}>{formatWOS(storeWOS)}</p>
          <p className="text-xs text-muted-foreground mt-2">Store units: {wosData.eoh_store?.toLocaleString() ?? '—'}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-600 mb-2">DC WOS</p>
          <p className={`text-3xl font-bold ${getWOSColor(dcWOS)}`}>{formatWOS(dcWOS)}</p>
          <p className="text-xs text-muted-foreground mt-2">DC units: {wosData.eoh_dc?.toLocaleString() ?? '—'}</p>
        </div>
      </div>

      {/* Status */}
      <div className="mt-6 flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center space-x-2">
          <div
            className={`w-2.5 h-2.5 rounded-full ${
              status === 'healthy'
                ? 'bg-green-500'
                : status === 'warning'
                ? 'bg-yellow-500'
                : status === 'critical'
                ? 'bg-red-500'
                : 'bg-gray-400'
            }`}
          ></div>
          <span className="text-sm font-medium text-gray-700 text-capitalize">{status}</span>
        </div>
        <div className="text-xs text-muted-foreground">
          Weekly units: {wosData.weekly_units?.toLocaleString() ?? '—'}
        </div>
      </div>

      {/* Deep Dive */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <button
          onClick={handleDeepDive}
          className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 transition-colors font-medium"
        >
          <ExternalLink className="w-4 h-4" />
          <span>Inventory Health Deep Dive</span>
        </button>
      </div>

      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 pt-2 border-t border-border text-xs text-muted-foreground">
          Week: {wosData.week} • Data version: {wosData.meta?.data_version ?? '1.0'}
        </div>
      )}
    </div>
  )
}

export default InventoryCoverageTile
